import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { plans as fallbackPlans, heroSlides as fallbackSlides, type Plan } from "@/content/site";

export type Banner = {
  id: string;
  titleStart: string;
  titleAccent: string;
  text: string;
  ctaLabel: string;
  ctaTo: string;
  imageUrl?: string | null;
};

export const plansQuery = {
  queryKey: ["plans"],
  queryFn: async (): Promise<Plan[]> => {
    const { data, error } = await supabase
      .from("plans")
      .select("*")
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error || !data?.length) return fallbackPlans;
    return data.map((p) => ({
      id: p.id,
      speed: p.speed,
      unit: p.unit,
      price: p.price,
      cents: p.cents,
      highlight: p.highlight,
      badge: p.badge ?? undefined,
      features: p.features ?? [],
    }));
  },
};

export const bannersQuery = {
  queryKey: ["banners"],
  queryFn: async (): Promise<Banner[]> => {
    const { data, error } = await supabase
      .from("banners")
      .select("*")
      .eq("active", true)
      .order("sort_order", { ascending: true });
    if (error || !data?.length) {
      return fallbackSlides.map((s) => ({
        id: s.id,
        titleStart: s.titleStart,
        titleAccent: s.titleAccent,
        text: s.text,
        ctaLabel: s.ctaLabel,
        ctaTo: s.ctaTo,
        imageUrl: null,
      }));
    }
    return data.map((b) => ({
      id: b.id,
      titleStart: b.title_start,
      titleAccent: b.title_accent,
      text: b.text ?? "",
      ctaLabel: b.cta_label,
      ctaTo: b.cta_to,
      imageUrl: b.image_url,
    }));
  },
};

export function usePlans() {
  return useQuery(plansQuery);
}

export function useBanners() {
  return useQuery(bannersQuery);
}

/** Resolve an image path/URL so it works on a static SPA.
 *  - data: URLs are returned as-is (base64, no bucket needed)
 *  - http(s) and root-relative URLs are returned as-is
 *  - storage paths fall back to Supabase public URL when available
 */
export function siteImageUrl(path?: string | null) {
  if (!path) return null;
  if (path.startsWith("data:")) return path;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return path;
  const { data } = supabase.storage.from("site-images").getPublicUrl(path);
  return data.publicUrl;
}
